import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Switch,
} from 'antd';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { ILookup } from '../../../../services/common/common.model';
import { useAppSelector, useAppDispatch } from '../../../../store/app.hooks';
import {
  getBULookup,
  getCompanyLookup,
  getTenantLookup,
} from '../../../../store/common/common.action';
import {
  clearBULookUp,
  clearCompanyLookUp,
  commonSelector,
} from '../../../../store/common/common.reducer';
import { IAddAzureSavingsPlansProps } from './addAzureSavingsPlans.model';
import {
  azureSavingsPlansSelector,
  clearAzureSavingsPlansGetById,
  clearAzureSavingsPlansMessages,
} from '../../../../store/azure/azureSavingsPlans/azureSavingsPlans.reducer';
import {
  getAzureSavingsPlansById,
  saveAzureSavingsPlans,
} from '../../../../store/azure/azureSavingsPlans/azureSavingsPlans.action';
import { IAzureSavingsPlans } from '../../../../services/azure/azureSavingsPlans/azureSavingsPlans.model';
import { validateMessages } from '../../../../common/constants/common';
import BreadCrumbs from '../../../../common/components/Breadcrumbs';
import { Page } from '../../../../common/constants/pageAction';
import { forEditModal, passDateToApi } from '../../../../common/helperFunction';
import { IInlineSearch } from '../../../../common/models/common';
import { globalSearchSelector } from '../../../../store/globalSearch/globalSearch.reducer';

const { Option } = Select;

const AddAzureSavingsPlansModal: React.FC<IAddAzureSavingsPlansProps> = (props) => {
  const azureSavingsPlans = useAppSelector(azureSavingsPlansSelector);
  const commonLookups = useAppSelector(commonSelector);
  const dispatch = useAppDispatch();
  const globalFilters = useAppSelector(globalSearchSelector);

  const { id, showModal, handleModalClose, refreshDataTable } = props;

  const isNew: boolean = id ? false : true;
  const title = useMemo(() => {
    return (
      <>
        {isNew ? 'Add ' : 'Edit '}
        <BreadCrumbs pageName={Page.AzureSavingsPlans} level={1} />
      </>
    );
  }, [isNew]);
  const submitButtonText = useMemo(() => {
    return isNew ? 'Save' : 'Update';
  }, [isNew]);

  const [form] = Form.useForm();

  let initialValues: IAzureSavingsPlans = {
    company_id: null,
    bu_id: null,
    tenant_id: null,
    resource_id: '',
    display_name: '',
    sku_name: '',
    term: '',
    billing_plan: '',
    billing_scope_id: '',
    billing_account_id: '',
    billing_profile_id: '',
    commitment_amount: null,
    commitment_currency_code: '',
    commitment_grain: '',
    applied_scope_type: '',
    user_friendly_applied_scope_type: '',
    applied_scope_display_name: '',
    applied_scope_subscription_id: '',
    applied_scope_resource_group_id: '',
    applied_scope_management_group_id: '',
    provisioning_state: '',
    display_provisioning_state: '',
    renew: false,
    purchase_date: null,
    benefit_start_date: null,
    effective_date: null,
    expiry_date: null,
    utilization_1_day: null,
    utilization_7_day: null,
    utilization_30_day: null,
    utilization_trend: '',
  };

  const onFinish = (values: any) => {
    const inputValues: IAzureSavingsPlans = {
      ...values,
      id: id ? +id : null,
    };
    inputValues.purchase_date = passDateToApi(inputValues.purchase_date, true);
    inputValues.benefit_start_date = passDateToApi(inputValues.benefit_start_date, true);
    inputValues.effective_date = passDateToApi(inputValues.effective_date, true);
    inputValues.expiry_date = passDateToApi(inputValues.expiry_date, true);
    dispatch(saveAzureSavingsPlans(inputValues));
  };

  const handleTenantChange = (tenantId: number) => {
    form.setFieldsValue({ tenant_id: tenantId, company_id: null, bu_id: null });
    if (tenantId) {
      dispatch(getCompanyLookup(tenantId));
      dispatch(clearBULookUp());
    } else {
      dispatch(clearCompanyLookUp());
      dispatch(clearBULookUp());
    }
  };

  const handleCompanyChange = (companyId: number) => {
    form.setFieldsValue({ company_id: companyId, bu_id: null });
    if (companyId) {
      dispatch(getBULookup(companyId));
    } else {
      dispatch(clearBULookUp());
    }
  };

  const handleBUChange = (buId: number) => {
    form.setFieldsValue({ bu_id: buId });
  };

  const fillValuesOnEdit = async (data: IAzureSavingsPlans) => {
    if (data.tenant_id) {
      await dispatch(getCompanyLookup(data.tenant_id));
    }
    if (data.company_id) {
      await dispatch(getBULookup(data.company_id));
    }
    if (data) {
      initialValues = {
        tenant_id: _.isNull(data.tenant_id) ? null : data.tenant_id,
        company_id: _.isNull(data.company_id) ? null : data.company_id,
        bu_id: _.isNull(data.bu_id) ? null : data.bu_id,
        resource_id: data.resource_id,
        display_name: data.display_name,
        sku_name: data.sku_name,
        term: data.term,
        billing_plan: data.billing_plan,
        billing_scope_id: data.billing_scope_id,
        billing_account_id: data.billing_account_id,
        billing_profile_id: data.billing_profile_id,
        commitment_amount: data.commitment_amount,
        commitment_currency_code: data.commitment_currency_code,
        commitment_grain: data.commitment_grain,
        applied_scope_type: data.applied_scope_type,
        user_friendly_applied_scope_type: data.user_friendly_applied_scope_type,
        applied_scope_display_name: data.applied_scope_display_name,
        applied_scope_subscription_id: data.applied_scope_subscription_id,
        applied_scope_resource_group_id: data.applied_scope_resource_group_id,
        applied_scope_management_group_id: data.applied_scope_management_group_id,
        provisioning_state: data.provisioning_state,
        display_provisioning_state: data.display_provisioning_state,
        renew: data.renew,
        purchase_date: _.isNull(data.purchase_date) ? null : forEditModal(data.purchase_date),
        benefit_start_date: _.isNull(data.benefit_start_date)
          ? null
          : forEditModal(data.benefit_start_date),
        effective_date: _.isNull(data.effective_date) ? null : forEditModal(data.effective_date),
        expiry_date: _.isNull(data.expiry_date) ? null : forEditModal(data.expiry_date),
        utilization_1_day: data.utilization_1_day,
        utilization_7_day: data.utilization_7_day,
        utilization_30_day: data.utilization_30_day,
        utilization_trend: data.utilization_trend,
      };
      form.setFieldsValue(initialValues);
    }
  };

  useEffect(() => {
    if (azureSavingsPlans.save.messages.length > 0) {
      if (azureSavingsPlans.save.hasErrors) {
        toast.error(azureSavingsPlans.save.messages.join(' '));
      } else {
        toast.success(azureSavingsPlans.save.messages.join(' '));
        handleModalClose();
        refreshDataTable();
      }
      dispatch(clearAzureSavingsPlansMessages());
    }
  }, [azureSavingsPlans.save.messages]);

  useEffect(() => {
    if (+id > 0 && azureSavingsPlans.getById.data) {
      const data = azureSavingsPlans.getById.data;
      fillValuesOnEdit(data);
    }
  }, [azureSavingsPlans.getById.data]);

  useEffect(() => {
    if (Object.keys(globalFilters?.globalTenantLookup?.data).length == 0) {
      dispatch(getTenantLookup());
    }
    if (+id > 0) {
      dispatch(getAzureSavingsPlansById(+id));
    }
    return () => {
      dispatch(clearAzureSavingsPlansGetById());
      dispatch(clearCompanyLookUp());
      dispatch(clearBULookUp());
    };
  }, [dispatch]);

  useEffect(() => {
    if (+id === 0) {
      const globalSearch: IInlineSearch = {};
      for (const key in globalFilters.search) {
        const element = globalFilters.search[key];
        globalSearch[key] = element ? [element] : null;
      }
      if (globalFilters.search.tenant_id && globalFilters.search.tenant_id !== 0) {
        if (!globalFilters.search.company_id) {
          dispatch(getCompanyLookup(globalSearch.tenant_id[0]));
        }
        if (!globalFilters.search.bu_id && globalFilters.search.company_id !== 0) {
          dispatch(getBULookup(globalSearch.company_id[0]));
        }
        const initlValues = {
          company_id: _.isNull(globalSearch.company_id) ? null : globalSearch.company_id[0],
          bu_id: _.isNull(globalSearch.bu_id) ? null : globalSearch.bu_id[0],
          tenant_id: _.isNull(globalSearch.tenant_id) ? null : globalSearch.tenant_id[0],
        };
        form.setFieldsValue(initlValues);
      }
    }
  }, []);

  return (
    <>
      <Modal
        wrapClassName="custom-modal"
        title={title}
        centered
        visible={showModal}
        onCancel={handleModalClose}
        footer={false}
      >
        {azureSavingsPlans.getById.loading ? (
          <div className="spin-loader">
            <Spin spinning={azureSavingsPlans.getById.loading} />
          </div>
        ) : (
          <Form
            form={form}
            name="addAzureSavingsPlans"
            initialValues={initialValues}
            onFinish={onFinish}
            validateMessages={validateMessages}
          >
            <Row gutter={[30, 15]} className="form-label-hide">
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Tenant
                  <Form.Item
                    name="tenant_id"
                    className="m-0"
                    label="Tenant"
                    rules={[{ required: true }]}
                  >
                    <Select
                      onChange={handleTenantChange}
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option: any) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA: any, optionB: any) =>
                        optionA.children
                          ?.toLowerCase()
                          ?.localeCompare(optionB.children?.toLowerCase())
                      }
                      loading={commonLookups.tenantLookup.loading}
                    >
                      {Object.keys(globalFilters?.globalTenantLookup?.data).length > 0
                        ? globalFilters?.globalTenantLookup?.data.map((option: ILookup) => (
                            <Option key={option.id} value={option.id}>
                              {option.name}
                            </Option>
                          ))
                        : commonLookups.tenantLookup.data.map((option: ILookup) => (
                            <Option key={option.id} value={option.id}>
                              {option.name}
                            </Option>
                          ))}
                    </Select>
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Company
                  <Form.Item
                    name="company_id"
                    className="m-0"
                    label="Company"
                    rules={[{ required: true }]}
                  >
                    <Select
                      onChange={handleCompanyChange}
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option: any) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA: any, optionB: any) =>
                        optionA.children
                          ?.toLowerCase()
                          ?.localeCompare(optionB.children?.toLowerCase())
                      }
                      loading={commonLookups.companyLookup.loading}
                    >
                      {Object.keys(commonLookups.companyLookup.data).length > 0
                        ? commonLookups.companyLookup.data.map((option: ILookup) => (
                            <Option key={option.id} value={option.id}>
                              {option.name}
                            </Option>
                          ))
                        : globalFilters?.globalCompanyLookup?.data.map((option: ILookup) => (
                            <Option key={option.id} value={option.id}>
                              {option.name}
                            </Option>
                          ))}
                    </Select>
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  BU
                  <Form.Item name="bu_id" className="m-0" label="BU">
                    <Select
                      onChange={handleBUChange}
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option: any) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA: any, optionB: any) =>
                        optionA.children
                          ?.toLowerCase()
                          ?.localeCompare(optionB.children?.toLowerCase())
                      }
                      loading={commonLookups.buLookup.loading}
                    >
                      {Object.keys(commonLookups.buLookup.data).length > 0
                        ? commonLookups.buLookup.data.map((option: ILookup) => (
                            <Option key={option.id} value={option.id}>
                              {option.name}
                            </Option>
                          ))
                        : globalFilters?.globalBULookup?.data.map((option: ILookup) => (
                            <Option key={option.id} value={option.id}>
                              {option.name}
                            </Option>
                          ))}
                    </Select>
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Resource Id
                  <Form.Item
                    name="resource_id"
                    className="m-0"
                    label="Resource Id"
                    rules={[{ required: true, max: 500 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Display Name
                  <Form.Item
                    name="display_name"
                    className="m-0"
                    label="Display Name"
                    rules={[{ max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Sku Name
                  <Form.Item
                    name="sku_name"
                    className="m-0"
                    label="Sku Name"
                    rules={[{ max: 100 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Term
                  <Form.Item name="term" className="m-0" label="Term" rules={[{ max: 10 }]}>
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Billing Plan
                  <Form.Item
                    name="billing_plan"
                    className="m-0"
                    label="Billing Plan"
                    rules={[{ max: 50 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Billing Scope Id
                  <Form.Item
                    name="billing_scope_id"
                    className="m-0"
                    label="Billing Scope Id"
                    rules={[{ max: 500 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Billing Account Id
                  <Form.Item
                    name="billing_account_id"
                    className="m-0"
                    label="Billing Account Id"
                    rules={[{ max: 500 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Billing Profile Id
                  <Form.Item
                    name="billing_profile_id"
                    className="m-0"
                    label="Billing Profile Id"
                    rules={[{ max: 500 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Commitment Amount
                  <Form.Item
                    name="commitment_amount"
                    label="Commitment Amount"
                    className="m-0"
                    rules={[{ type: 'number' }]}
                  >
                    <InputNumber className="w-100 form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Commitment Currency Code
                  <Form.Item
                    name="commitment_currency_code"
                    className="m-0"
                    label="Commitment Currency Code"
                    rules={[{ max: 3 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Commitment Grain
                  <Form.Item
                    name="commitment_grain"
                    className="m-0"
                    label="Commitment Grain"
                    rules={[{ max: 20 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Applied Scope Type
                  <Form.Item
                    name="applied_scope_type"
                    className="m-0"
                    label="Applied Scope Type"
                    rules={[{ max: 50 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  User Friendly Applied Scope Type
                  <Form.Item
                    name="user_friendly_applied_scope_type"
                    className="m-0"
                    label="User Friendly Applied Scope Type"
                    rules={[{ max: 50 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Applied Scope Display Name
                  <Form.Item
                    name="applied_scope_display_name"
                    className="m-0"
                    label="Applied Scope Display Name"
                    rules={[{ max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Applied Scope Subscription Id
                  <Form.Item
                    name="applied_scope_subscription_id"
                    className="m-0"
                    label="Applied Scope Subscription Id"
                    rules={[{ max: 500 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Applied Scope Resource Group Id
                  <Form.Item
                    name="applied_scope_resource_group_id"
                    className="m-0"
                    label="Applied Scope Resource Group Id"
                    rules={[{ max: 500 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Applied Scope Management Group Id
                  <Form.Item
                    name="applied_scope_management_group_id"
                    className="m-0"
                    label="Applied Scope Management Group Id"
                    rules={[{ max: 500 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Provisioning State
                  <Form.Item
                    name="provisioning_state"
                    className="m-0"
                    label="Provisioning State"
                    rules={[{ max: 50 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Display Provisioning State
                  <Form.Item
                    name="display_provisioning_state"
                    className="m-0"
                    label="Display Provisioning State"
                    rules={[{ max: 50 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group form-inline-pt m-0">
                  <Form.Item name="renew" className="m-0" valuePropName="checked">
                    <Switch className="form-control" />
                  </Form.Item>
                  &nbsp;Renew
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Purchase Date
                  <Form.Item name="purchase_date" label="Purchase Date" className="m-0">
                    <DatePicker className="form-control w-100" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Benefit Start Date
                  <Form.Item name="benefit_start_date" label="Benefit Start Date" className="m-0">
                    <DatePicker className="form-control w-100" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Effective Date
                  <Form.Item name="effective_date" label="Effective Date" className="m-0">
                    <DatePicker className="form-control w-100" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Expiry Date
                  <Form.Item name="expiry_date" label="Expiry Date" className="m-0">
                    <DatePicker className="form-control w-100" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Utilization 1 Day %
                  <Form.Item
                    name="utilization_1_day"
                    label="Utilization 1 Day %"
                    className="m-0"
                    rules={[{ type: 'number' }]}
                  >
                    <InputNumber className="w-100 form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Utilization 7 Day %
                  <Form.Item
                    name="utilization_7_day"
                    label="Utilization 7 Day %"
                    className="m-0"
                    rules={[{ type: 'number' }]}
                  >
                    <InputNumber className="w-100 form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Utilization 30 Day %
                  <Form.Item
                    name="utilization_30_day"
                    label="Utilization 30 Day %"
                    className="m-0"
                    rules={[{ type: 'number' }]}
                  >
                    <InputNumber className="w-100 form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  Utilization Trend
                  <Form.Item
                    name="utilization_trend"
                    className="m-0"
                    label="Utilization Trend"
                    rules={[{ max: 20 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
            </Row>
            <div className="btns-block modal-footer">
              <Button
                key="submit"
                type="primary"
                htmlType="submit"
                loading={azureSavingsPlans.save.loading}
              >
                {submitButtonText}
              </Button>
              <Button key="back" onClick={handleModalClose}>
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    </>
  );
};
export default AddAzureSavingsPlansModal;
